import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import RecordDetailModal from '../components/RecordDetailModal';
import ExportModal from '../components/ExportModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Upload, Download, ChevronLeft, ChevronRight, Eye, FileDown, ArrowUpDown, Trash2, CalendarX2, CheckSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DataManagement = ({ user, onLogout }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const pageSize = 50;

  // Delete states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState(''); // 'dates', 'selected', 'all'
  const [datesToDelete, setDatesToDelete] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/records`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, page_size: pageSize, sort_field: sortField, sort_order: sortOrder },
      });
      setRecords(response.data.records);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch records');
    } finally {
      setLoading(false);
    }
  }, [page, sortField, sortOrder]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/records/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`${response.data.records_inserted} records uploaded successfully!`);
      setPage(1);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = 'Date,Website,Offer,Credit Score,Verification ID,First Name,Last Name,Agent,Time,Reason,Address,City,State,Zipcode,Email,Phone\n';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'loan_records_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('Template downloaded!');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const viewDetails = (record) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  // Delete functions
  const openDeleteByDates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/records/dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableDates(res.data.dates || []);
      setDatesToDelete([]);
      setDeleteMode('dates');
      setDeleteDialogOpen(true);
    } catch (e) {
      toast.error('Failed to fetch dates');
    }
  };

  const toggleDateSelection = (date) => {
    setDatesToDelete(prev => 
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  const selectAllDates = () => {
    if (datesToDelete.length === availableDates.length) {
      setDatesToDelete([]);
    } else {
      setDatesToDelete(availableDates.map(d => d.date));
    }
  };

  const handleDeleteByDates = async () => {
    if (datesToDelete.length === 0) { toast.error('Select at least one date'); return; }
    const totalCount = availableDates.filter(d => datesToDelete.includes(d.date)).reduce((s, d) => s + d.count, 0);
    if (!window.confirm(`Are you sure you want to delete ${totalCount.toLocaleString()} records for ${datesToDelete.length} date(s)? This action CANNOT be undone.`)) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/records/delete-by-dates`, 
        { dates: datesToDelete },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setDeleteDialogOpen(false);
      setSelectedRecordIds([]);
      setSelectMode(false);
      fetchRecords();
    } catch (e) {
      toast.error('Failed to delete records');
    } finally {
      setDeleting(false);
    }
  };

  const toggleRecordSelect = (id) => {
    setSelectedRecordIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllOnPage = () => {
    const pageIds = records.map(r => r.id);
    const allSelected = pageIds.every(id => selectedRecordIds.includes(id));
    if (allSelected) {
      setSelectedRecordIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedRecordIds(prev => [...new Set([...prev, ...pageIds])]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRecordIds.length === 0) { toast.error('No records selected'); return; }
    if (!window.confirm(`Are you sure you want to delete ${selectedRecordIds.length} selected record(s)? This action CANNOT be undone.`)) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/records/delete-selected`,
        { ids: selectedRecordIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setSelectedRecordIds([]);
      setSelectMode(false);
      fetchRecords();
    } catch (e) {
      toast.error('Failed to delete records');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`⚠️ DANGER: Are you sure you want to delete ALL ${total.toLocaleString()} records? This action CANNOT be undone.`)) return;
    if (!window.confirm(`⚠️ FINAL WARNING: This will permanently delete ALL data. Type OK to confirm.`)) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${BACKEND_URL}/api/records/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setDeleteDialogOpen(false);
      setSelectedRecordIds([]);
      setSelectMode(false);
      fetchRecords();
    } catch (e) {
      toast.error('Failed to delete records');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-8 overflow-auto" data-testid="data-management">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-primary mb-2">Data Management</h1>
            <p className="text-muted-foreground">Upload and manage loan records</p>
          </div>

          <Card className="mb-6 border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-3">
                  <label htmlFor="file-upload">
                    <Button
                      className="cursor-pointer"
                      disabled={uploading}
                      data-testid="upload-button"
                      asChild
                    >
                      <span>
                        <Upload className="mr-2 h-4 w-4" />
                        {uploading ? 'Uploading...' : 'Upload File'}
                      </span>
                    </Button>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".csv,.xlsx"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  <Button variant="outline" onClick={downloadTemplate} data-testid="template-button">
                    <FileDown className="mr-2 h-4 w-4" />
                    Download Template
                  </Button>
                  {user.role === 'admin' && (
                    <Button 
                      variant="outline" 
                      onClick={() => setExportOpen(true)} 
                      data-testid="export-button"
                      className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export Options
                    </Button>
                  )}
                  {user.role === 'admin' && (
                    <>
                      <Button variant="outline" onClick={openDeleteByDates} className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200" data-testid="delete-by-date-btn">
                        <CalendarX2 className="mr-2 h-4 w-4" />
                        Delete by Date
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => { setSelectMode(!selectMode); setSelectedRecordIds([]); }}
                        className={selectMode ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"}
                        data-testid="select-mode-btn"
                      >
                        <CheckSquare className="mr-2 h-4 w-4" />
                        {selectMode ? 'Cancel Select' : 'Select Records'}
                      </Button>
                      <Button variant="outline" onClick={() => { setDeleteMode('all'); setDeleteDialogOpen(true); }} className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200" data-testid="delete-all-btn">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete All
                      </Button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {selectMode && selectedRecordIds.length > 0 && (
                    <Button size="sm" onClick={handleDeleteSelected} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-selected-btn">
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete {selectedRecordIds.length} Selected
                    </Button>
                  )}
                  <p className="text-sm text-muted-foreground" data-testid="total-records">
                    Total: <span className="font-semibold text-primary text-lg">{total.toLocaleString()}</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50/50 backdrop-blur-sm sticky top-0 z-10 border-b">
                    <TableRow>
                      {selectMode && (
                        <TableHead className="w-10">
                          <input type="checkbox" className="w-4 h-4 accent-blue-600 cursor-pointer"
                            checked={records.length > 0 && records.every(r => selectedRecordIds.includes(r.id))}
                            onChange={selectAllOnPage} />
                        </TableHead>
                      )}
                      <TableHead className="font-semibold cursor-pointer hover:bg-slate-100" onClick={() => handleSort('date')}>
                        <div className="flex items-center gap-1">
                          Date <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </TableHead>
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Phone</TableHead>
                      <TableHead className="font-semibold">Offer</TableHead>
                      <TableHead className="font-semibold">Credit Score</TableHead>
                      <TableHead className="font-semibold">Agent</TableHead>
                      <TableHead className="font-semibold">City</TableHead>
                      <TableHead className="font-semibold">State</TableHead>
                      <TableHead className="font-semibold text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={selectMode ? 11 : 10} className="text-center py-8 text-muted-foreground">
                          Loading records...
                        </TableCell>
                      </TableRow>
                    ) : records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={selectMode ? 11 : 10} className="text-center py-8 text-muted-foreground">
                          No records found. Upload a file to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      records.map((record, index) => (
                        <TableRow key={record.id} className={`hover:bg-slate-50/80 transition-colors ${selectedRecordIds.includes(record.id) ? 'bg-blue-50' : ''}`} data-testid={`record-row-${index}`}>
                          {selectMode && (
                            <TableCell>
                              <input type="checkbox" data-testid={`record-checkbox-${index}`} className="w-4 h-4 accent-blue-600 cursor-pointer"
                                checked={selectedRecordIds.includes(record.id)}
                                onChange={() => toggleRecordSelect(record.id)} />
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-xs">{record.date}</TableCell>
                          <TableCell className="font-medium">
                            {record.first_name} {record.last_name}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{record.email}</TableCell>
                          <TableCell className="font-mono text-xs">{record.phone}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                              {record.offer}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-sm font-semibold">{record.credit_score}</TableCell>
                          <TableCell>{record.agent}</TableCell>
                          <TableCell>{record.city}</TableCell>
                          <TableCell>{record.state}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewDetails(record)}
                              data-testid={`view-detail-${index}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  data-testid="prev-page-button"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  data-testid="next-page-button"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </main>
      <RecordDetailModal record={selectedRecord} open={detailOpen} onClose={() => setDetailOpen(false)} />
      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} userRole={user.role} />
      
      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {deleteMode === 'dates' ? 'Delete Records by Date' : 'Delete All Records'}
            </DialogTitle>
          </DialogHeader>
          
          {deleteMode === 'dates' && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm text-slate-600">
                  Select dates to delete ({datesToDelete.length} selected)
                </Label>
                <Button variant="ghost" size="sm" onClick={selectAllDates} className="text-xs">
                  {datesToDelete.length === availableDates.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="flex-1 overflow-auto border rounded-lg">
                {availableDates.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No dates found</p>
                ) : (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left w-10">
                          <input type="checkbox" className="w-4 h-4 accent-red-600 cursor-pointer"
                            checked={datesToDelete.length === availableDates.length && availableDates.length > 0}
                            onChange={selectAllDates} />
                        </th>
                        <th className="px-3 py-2 text-left text-sm font-semibold text-slate-600">Date</th>
                        <th className="px-3 py-2 text-right text-sm font-semibold text-slate-600">Records</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableDates.map(d => (
                        <tr key={d.date} data-testid={`date-row-${d.date}`} className={`border-b cursor-pointer hover:bg-red-50 ${datesToDelete.includes(d.date) ? 'bg-red-50' : ''}`}
                          onClick={() => toggleDateSelection(d.date)}>
                          <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" data-testid={`date-checkbox-${d.date}`} className="w-4 h-4 accent-red-600 cursor-pointer"
                              checked={datesToDelete.includes(d.date)}
                              onChange={() => toggleDateSelection(d.date)} />
                          </td>
                          <td className="px-3 py-2 font-mono text-sm">{d.date}</td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-700">{d.count.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {datesToDelete.length > 0 && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm font-medium">
                    Will delete {availableDates.filter(d => datesToDelete.includes(d.date)).reduce((s, d) => s + d.count, 0).toLocaleString()} records for {datesToDelete.length} date(s)
                  </p>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Button variant="outline" className="flex-1" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button data-testid="confirm-delete-dates-btn" className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteByDates} disabled={deleting || datesToDelete.length === 0}>
                  {deleting ? 'Deleting...' : `Delete ${datesToDelete.length} Date(s)`}
                </Button>
              </div>
            </div>
          )}
          
          {deleteMode === 'all' && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 font-semibold text-lg">⚠ This will permanently delete ALL {total.toLocaleString()} records</p>
                <p className="text-red-600 text-sm mt-1">This action cannot be undone. All data, stats, dashboards, and leaderboards will be reset.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteAll} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataManagement;